import { createFileRoute, Link } from "@tanstack/react-router";
import {
	AlertCircle,
	ArrowLeft,
	Calendar,
	Clock,
	Edit,
	Image,
	MapPin,
	Plus,
	Repeat,
	Trash2,
	User,
	Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute(
	"/_auth/app/_app/help/user-guide/schedule-management",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();

	return (
		<div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6 sm:space-y-8">
			{/* Mobile Back Button */}
			<Link
				to="/app/help/user-guide"
				className="md:hidden flex items-center gap-2 text-primary"
			>
				<ArrowLeft className="w-4 h-4" />
				{t("common.back")}
			</Link>

			{/* Header */}
			<div className="mb-6 sm:mb-8">
				<div className="flex items-center space-x-3 mb-4">
					<div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-50 rounded-lg flex items-center justify-center shrink-0">
						<Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
					</div>
					<div className="min-w-0">
						<h1 className="text-xl sm:text-3xl font-bold text-base-content">
							{t("help.scheduleManagement")}
						</h1>
						<p className="text-sm sm:text-lg text-base-content/70">
							{t("help.scheduleManagementDescription")}
						</p>
					</div>
				</div>
			</div>

			{/* Overview */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Calendar className="h-5 w-5" />
						<span>{t("help.scheduleGuideOverviewTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.scheduleGuideOverviewText")}
					</p>
					<div className="bg-info/10 border border-info/20 rounded-lg p-4">
						<div className="flex items-start space-x-3">
							<AlertCircle className="h-5 w-5 text-info mt-0.5" />
							<div>
								<h4 className="font-medium text-info">
									{t("help.scheduleGuideKeyFeatures")}
								</h4>
								<ul className="text-sm text-info/80 mt-2 space-y-1">
									<li>• {t("help.scheduleGuideKeyFeature1")}</li>
									<li>• {t("help.scheduleGuideKeyFeature2")}</li>
									<li>• {t("help.scheduleGuideKeyFeature3")}</li>
									<li>• {t("help.scheduleGuideKeyFeature4")}</li>
								</ul>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Accessing Schedule Management */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Calendar className="h-5 w-5" />
						<span>{t("help.scheduleGuideAccessingTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.scheduleGuideAccessingText")}
					</p>
					<ol className="list-decimal list-inside space-y-2 text-base-content/80">
						<li>{t("help.scheduleGuideAccessingStep1")}</li>
						<li>{t("help.scheduleGuideAccessingStep2")}</li>
						<li>{t("help.scheduleGuideAccessingStep3")}</li>
					</ol>
					<div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
						<div className="flex items-start space-x-3">
							<AlertCircle className="h-5 w-5 text-warning mt-0.5" />
							<div>
								<h4 className="font-medium text-warning">
									{t("help.scheduleGuidePermissionTitle")}
								</h4>
								<p className="text-sm text-warning/80 mt-1">
									{t("help.scheduleGuidePermissionText")}
								</p>
							</div>
						</div>
					</div>
					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.scheduleGuideImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Understanding Schedule Types */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Users className="h-5 w-5" />
						<span>{t("help.scheduleGuideTypesTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.scheduleGuideTypesText")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideTypesOptionsTitle")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-3">
									<div className="bg-success/10 border border-success/20 rounded-lg p-4">
										<h5 className="font-medium text-success flex items-center mb-2">
											<Users className="h-4 w-4 mr-2" />
											{t("help.scheduleGuideGroupTitle")}
										</h5>
										<ul className="text-sm text-success/80 space-y-1">
											<li>• {t("help.scheduleGuideGroupFeature1")}</li>
											<li>• {t("help.scheduleGuideGroupFeature2")}</li>
											<li>• {t("help.scheduleGuideGroupFeature3")}</li>
											<li>• {t("help.scheduleGuideGroupFeature4")}</li>
										</ul>
									</div>
								</div>
								<div className="space-y-3">
									<div className="bg-info/10 border border-info/20 rounded-lg p-4">
										<h5 className="font-medium text-info flex items-center mb-2">
											<User className="h-4 w-4 mr-2" />
											{t("help.scheduleGuideIndividualTitle")}
										</h5>
										<ul className="text-sm text-info/80 space-y-1">
											<li>• {t("help.scheduleGuideIndividualFeature1")}</li>
											<li>• {t("help.scheduleGuideIndividualFeature2")}</li>
											<li>• {t("help.scheduleGuideIndividualFeature3")}</li>
											<li>• {t("help.scheduleGuideIndividualFeature4")}</li>
										</ul>
									</div>
								</div>
							</div>
						</div>

						<div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<AlertCircle className="h-5 w-5 text-warning mt-0.5" />
								<div>
									<h5 className="font-medium text-warning">
										{t("help.scheduleGuideTypesImportantNote")}
									</h5>
									<p className="text-sm text-warning/80 mt-1">
										{t("help.scheduleGuideTypesImportantText")}
									</p>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.scheduleGuideTypesImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Recurrence Patterns */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Repeat className="h-5 w-5" />
						<span>{t("help.scheduleGuideRecurrenceTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.scheduleGuideRecurrenceText")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideRecurrenceTypesTitle")}
							</h4>
							<div className="space-y-3">
								<div className="bg-base-100 border border-base-200 rounded-lg p-4">
									<h5 className="font-medium text-base-content flex items-center mb-2">
										<Calendar className="h-4 w-4 mr-2 text-blue-600" />
										{t("help.scheduleGuideSingleTitle")}
									</h5>
									<ul className="text-sm text-base-content/70 space-y-1">
										<li>• {t("help.scheduleGuideSingleFeature1")}</li>
										<li>• {t("help.scheduleGuideSingleFeature2")}</li>
										<li>• {t("help.scheduleGuideSingleFeature3")}</li>
										<li>• {t("help.scheduleGuideSingleFeature4")}</li>
									</ul>
								</div>
								<div className="bg-base-100 border border-base-200 rounded-lg p-4">
									<h5 className="font-medium text-base-content flex items-center mb-2">
										<Repeat className="h-4 w-4 mr-2 text-green-600" />
										{t("help.scheduleGuideWeeklyTitle")}
									</h5>
									<ul className="text-sm text-base-content/70 space-y-1">
										<li>• {t("help.scheduleGuideWeeklyFeature1")}</li>
										<li>• {t("help.scheduleGuideWeeklyFeature2")}</li>
										<li>• {t("help.scheduleGuideWeeklyFeature3")}</li>
										<li>• {t("help.scheduleGuideWeeklyFeature4")}</li>
									</ul>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideDaySelectionTitle")}
							</h4>
							<p className="text-sm text-base-content/70 mb-3">
								{t("help.scheduleGuideDaySelectionText")}
							</p>
							<div className="bg-success/10 border border-success/20 rounded-lg p-3">
								<p className="text-sm text-success/80">
									{t("help.scheduleGuideDaySelectionExample")}
								</p>
							</div>
						</div>

						<div className="bg-error/10 border border-error/20 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<AlertCircle className="h-5 w-5 text-error mt-0.5" />
								<div>
									<h5 className="font-medium text-error">
										{t("help.scheduleGuideEditingRestrictionsTitle")}
									</h5>
									<p className="text-sm text-error/80 mt-1">
										{t("help.scheduleGuideEditingRestrictionsText")}
									</p>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.scheduleGuideRecurrenceImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Creating New Schedules */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Plus className="h-5 w-5" />
						<span>{t("help.scheduleGuideCreatingTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.scheduleGuideCreatingText")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideCreatingStep1Title")}
							</h4>
							<ol className="list-decimal list-inside space-y-1 text-sm text-base-content/70">
								<li>{t("help.scheduleGuideCreatingStep1Text1")}</li>
								<li>{t("help.scheduleGuideCreatingStep1Text2")}</li>
								<li>{t("help.scheduleGuideCreatingStep1Text3")}</li>
							</ol>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideCreatingSection1Title")}
							</h4>
							<div className="bg-base-100 border border-base-200 rounded-lg p-4">
								<h5 className="font-medium text-sm text-base-content mb-2 flex items-center">
									<Calendar className="h-4 w-4 mr-2" />
									{t("help.scheduleGuideCreatingSection1Subtitle")}
								</h5>
								<ul className="text-sm text-base-content/70 space-y-1">
									<li>• {t("help.scheduleGuideCreatingSection1Feature1")}</li>
									<li>• {t("help.scheduleGuideCreatingSection1Feature2")}</li>
									<li>• {t("help.scheduleGuideCreatingSection1Feature3")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideCreatingSection2Title")}
							</h4>
							<div className="bg-base-100 border border-base-200 rounded-lg p-4">
								<h5 className="font-medium text-sm text-base-content mb-2 flex items-center">
									<MapPin className="h-4 w-4 mr-2" />
									{t("help.scheduleGuideCreatingSection2Subtitle")}
								</h5>
								<ul className="text-sm text-base-content/70 space-y-1">
									<li>• {t("help.scheduleGuideCreatingSection2Feature1")}</li>
									<li>• {t("help.scheduleGuideCreatingSection2Feature2")}</li>
									<li>• {t("help.scheduleGuideCreatingSection2Feature3")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideCreatingSection3Title")}
							</h4>
							<div className="bg-base-100 border border-base-200 rounded-lg p-4">
								<h5 className="font-medium text-sm text-base-content mb-2 flex items-center">
									<Clock className="h-4 w-4 mr-2" />
									{t("help.scheduleGuideCreatingSection3Subtitle")}
								</h5>
								<ul className="text-sm text-base-content/70 space-y-1">
									<li>• {t("help.scheduleGuideCreatingSection3Feature1")}</li>
									<li>• {t("help.scheduleGuideCreatingSection3Feature2")}</li>
									<li>• {t("help.scheduleGuideCreatingSection3Feature3")}</li>
									<li>• {t("help.scheduleGuideCreatingSection3Feature4")}</li>
									<li>• {t("help.scheduleGuideCreatingSection3Feature5")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideCreatingSection4Title")}
							</h4>
							<div className="bg-base-100 border border-base-200 rounded-lg p-4">
								<h5 className="font-medium text-sm text-base-content mb-2 flex items-center">
									<Repeat className="h-4 w-4 mr-2" />
									{t("help.scheduleGuideCreatingSection4Subtitle")}
								</h5>
								<ul className="text-sm text-base-content/70 space-y-1">
									<li>• {t("help.scheduleGuideCreatingSection4Feature1")}</li>
									<li>• {t("help.scheduleGuideCreatingSection4Feature2")}</li>
									<li>• {t("help.scheduleGuideCreatingSection4Feature3")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideValidationTitle")}
							</h4>
							<div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
								<div className="flex items-start space-x-3">
									<AlertCircle className="h-5 w-5 text-warning mt-0.5" />
									<div>
										<h5 className="font-medium text-warning">
											{t("help.scheduleGuideValidationSubtitle")}
										</h5>
										<ul className="text-sm text-warning/80 mt-2 space-y-1">
											<li>• {t("help.scheduleGuideValidationRule1")}</li>
											<li>• {t("help.scheduleGuideValidationRule2")}</li>
											<li>• {t("help.scheduleGuideValidationRule3")}</li>
											<li>• {t("help.scheduleGuideValidationRule4")}</li>
											<li>• {t("help.scheduleGuideValidationRule5")}</li>
										</ul>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.scheduleGuideCreatingImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Schedule List and Management */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Calendar className="h-5 w-5" />
						<span>{t("help.scheduleGuideListTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.scheduleGuideListText")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideListColumnsTitle")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.scheduleGuideListColumn1Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.scheduleGuideListColumn1Text")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.scheduleGuideListColumn2Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.scheduleGuideListColumn2Text")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.scheduleGuideListColumn3Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.scheduleGuideListColumn3Text")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.scheduleGuideListColumn4Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.scheduleGuideListColumn4Text")}
										</p>
									</div>
								</div>
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.scheduleGuideListColumn5Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.scheduleGuideListColumn5Text")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.scheduleGuideListColumn6Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.scheduleGuideListColumn6Text")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.scheduleGuideListColumn7Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.scheduleGuideListColumn7Text")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.scheduleGuideListColumn8Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.scheduleGuideListColumn8Text")}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideListActionsTitle")}
							</h4>
							<div className="space-y-2">
								<div>
									<h5 className="font-medium text-sm text-base-content flex items-center">
										<Edit className="h-4 w-4 mr-2" />
										{t("help.scheduleGuideListEditTitle")}
									</h5>
									<p className="text-sm text-base-content/70">
										{t("help.scheduleGuideListEditText")}
									</p>
								</div>
								<div>
									<h5 className="font-medium text-sm text-base-content flex items-center">
										<Trash2 className="h-4 w-4 mr-2" />
										{t("help.scheduleGuideListDeleteTitle")}
									</h5>
									<p className="text-sm text-base-content/70">
										{t("help.scheduleGuideListDeleteText")}
									</p>
								</div>
							</div>
						</div>

						<div className="bg-info/10 border border-info/20 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<AlertCircle className="h-5 w-5 text-info mt-0.5" />
								<div>
									<h5 className="font-medium text-info">
										{t("help.scheduleGuideListEmptyTitle")}
									</h5>
									<p className="text-sm text-info/80 mt-1">
										{t("help.scheduleGuideListEmptyText")}
									</p>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.scheduleGuideListImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Editing Schedules */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Edit className="h-5 w-5" />
						<span>{t("help.scheduleGuideEditingTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.scheduleGuideEditingText")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideEditingAccessTitle")}
							</h4>
							<ol className="list-decimal list-inside space-y-1 text-sm text-base-content/70">
								<li>{t("help.scheduleGuideEditingAccessStep1")}</li>
								<li>{t("help.scheduleGuideEditingAccessStep2")}</li>
								<li>{t("help.scheduleGuideEditingAccessStep3")}</li>
								<li>{t("help.scheduleGuideEditingAccessStep4")}</li>
							</ol>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideEditingFieldsTitle")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-3">
									<div className="bg-success/10 border border-success/20 rounded-lg p-4">
										<h5 className="font-medium text-success mb-2">
											{t("help.scheduleGuideEditingEditableTitle")}
										</h5>
										<ul className="text-sm text-success/80 space-y-1">
											<li>• {t("help.scheduleGuideEditingEditableField1")}</li>
											<li>• {t("help.scheduleGuideEditingEditableField2")}</li>
											<li>• {t("help.scheduleGuideEditingEditableField3")}</li>
											<li>• {t("help.scheduleGuideEditingEditableField4")}</li>
											<li>• {t("help.scheduleGuideEditingEditableField5")}</li>
											<li>• {t("help.scheduleGuideEditingEditableField6")}</li>
											<li>• {t("help.scheduleGuideEditingEditableField7")}</li>
										</ul>
									</div>
								</div>
								<div className="space-y-3">
									<div className="bg-error/10 border border-error/20 rounded-lg p-4">
										<h5 className="font-medium text-error mb-2">
											{t("help.scheduleGuideEditingProtectedTitle")}
										</h5>
										<ul className="text-sm text-error/80 space-y-1">
											<li>• {t("help.scheduleGuideEditingProtectedField1")}</li>
											<li>• {t("help.scheduleGuideEditingProtectedField2")}</li>
											<li>• {t("help.scheduleGuideEditingProtectedField3")}</li>
											<li>• {t("help.scheduleGuideEditingProtectedField4")}</li>
										</ul>
										<p className="text-sm text-error/80 mt-2">
											{t("help.scheduleGuideEditingProtectedText")}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideEditingConfirmationTitle")}
							</h4>
							<p className="text-sm text-base-content/70 mb-3">
								{t("help.scheduleGuideEditingConfirmationText")}
							</p>
							<div className="space-y-2">
								<div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
									<h5 className="font-medium text-warning text-sm mb-1">
										{t("help.scheduleGuideEditingTimeChangesTitle")}
									</h5>
									<p className="text-sm text-warning/80">
										{t("help.scheduleGuideEditingTimeChangesText")}
									</p>
								</div>
								<div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
									<h5 className="font-medium text-warning text-sm mb-1">
										{t("help.scheduleGuideEditingEndDateTitle")}
									</h5>
									<p className="text-sm text-warning/80">
										{t("help.scheduleGuideEditingEndDateText")}
									</p>
								</div>
								<div className="bg-info/10 border border-info/20 rounded-lg p-3">
									<h5 className="font-medium text-info text-sm mb-1">
										{t("help.scheduleGuideEditingDayCountTitle")}
									</h5>
									<p className="text-sm text-info/80">
										{t("help.scheduleGuideEditingDayCountText")}
									</p>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.scheduleGuideEditingImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Schedule Deletion */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Trash2 className="h-5 w-5" />
						<span>{t("help.scheduleGuideDeletionTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.scheduleGuideDeletionText")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideDeletionProcessTitle")}
							</h4>
							<ol className="list-decimal list-inside space-y-1 text-sm text-base-content/70">
								<li>{t("help.scheduleGuideDeletionProcessStep1")}</li>
								<li>{t("help.scheduleGuideDeletionProcessStep2")}</li>
								<li>{t("help.scheduleGuideDeletionProcessStep3")}</li>
								<li>{t("help.scheduleGuideDeletionProcessStep4")}</li>
							</ol>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideDeletionImpactTitle")}
							</h4>
							<p className="text-sm text-base-content/70 mb-3">
								{t("help.scheduleGuideDeletionImpactText")}
							</p>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.scheduleGuideDeletionImpact1")}</li>
								<li>• {t("help.scheduleGuideDeletionImpact2")}</li>
								<li>• {t("help.scheduleGuideDeletionImpact3")}</li>
								<li>• {t("help.scheduleGuideDeletionImpact4")}</li>
							</ul>
						</div>

						<div className="bg-error/10 border border-error/20 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<AlertCircle className="h-5 w-5 text-error mt-0.5" />
								<div>
									<h5 className="font-medium text-error">
										{t("help.scheduleGuideDeletionImportantTitle")}
									</h5>
									<ul className="text-sm text-error/80 mt-2 space-y-1">
										<li>• {t("help.scheduleGuideDeletionImportant1")}</li>
										<li>• {t("help.scheduleGuideDeletionImportant2")}</li>
										<li>• {t("help.scheduleGuideDeletionImportant3")}</li>
										<li>• {t("help.scheduleGuideDeletionImportant4")}</li>
										<li>• {t("help.scheduleGuideDeletionImportant5")}</li>
									</ul>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.scheduleGuideDeletionImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Schedule Details and Tabs */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Edit className="h-5 w-5" />
						<span>{t("help.scheduleGuideDetailsTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.scheduleGuideDetailsText")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideDetailsTabsTitle")}
							</h4>
							<p className="text-sm text-base-content/70 mb-3">
								{t("help.scheduleGuideDetailsTabsText")}
							</p>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-3">
									<div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
										<h5 className="font-medium text-primary flex items-center mb-2">
											<Edit className="h-4 w-4 mr-2" />
											{t("help.scheduleGuideDetailsEditTabTitle")}
										</h5>
										<ul className="text-sm text-primary/80 space-y-1">
											<li>• {t("help.scheduleGuideDetailsEditTabFeature1")}</li>
											<li>• {t("help.scheduleGuideDetailsEditTabFeature2")}</li>
											<li>• {t("help.scheduleGuideDetailsEditTabFeature3")}</li>
											<li>• {t("help.scheduleGuideDetailsEditTabFeature4")}</li>
										</ul>
									</div>
									<div className="bg-info/10 border border-info/20 rounded-lg p-3">
										<h5 className="font-medium text-info flex items-center mb-2">
											<Calendar className="h-4 w-4 mr-2" />
											{t("help.scheduleGuideDetailsSessionsTabTitle")}
										</h5>
										<ul className="text-sm text-info/80 space-y-1">
											<li>
												• {t("help.scheduleGuideDetailsSessionsTabFeature1")}
											</li>
											<li>
												• {t("help.scheduleGuideDetailsSessionsTabFeature2")}
											</li>
											<li>
												• {t("help.scheduleGuideDetailsSessionsTabFeature3")}
											</li>
											<li>
												• {t("help.scheduleGuideDetailsSessionsTabFeature4")}
											</li>
										</ul>
									</div>
								</div>
								<div className="space-y-3">
									<div className="bg-success/10 border border-success/20 rounded-lg p-3">
										<h5 className="font-medium text-success flex items-center mb-2">
											<Users className="h-4 w-4 mr-2" />
											{t("help.scheduleGuideDetailsEnrollmentsTabTitle")}
										</h5>
										<ul className="text-sm text-success/80 space-y-1">
											<li>
												• {t("help.scheduleGuideDetailsEnrollmentsTabFeature1")}
											</li>
											<li>
												• {t("help.scheduleGuideDetailsEnrollmentsTabFeature2")}
											</li>
											<li>
												• {t("help.scheduleGuideDetailsEnrollmentsTabFeature3")}
											</li>
											<li>
												• {t("help.scheduleGuideDetailsEnrollmentsTabFeature4")}
											</li>
										</ul>
									</div>
									<div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
										<h5 className="font-medium text-warning flex items-center mb-2">
											<Repeat className="h-4 w-4 mr-2" />
											{t("help.scheduleGuideDetailsMigrateTabTitle")}
										</h5>
										<ul className="text-sm text-warning/80 space-y-1">
											<li>
												• {t("help.scheduleGuideDetailsMigrateTabFeature1")}
											</li>
											<li>
												• {t("help.scheduleGuideDetailsMigrateTabFeature2")}
											</li>
											<li>
												• {t("help.scheduleGuideDetailsMigrateTabFeature3")}
											</li>
											<li>
												• {t("help.scheduleGuideDetailsMigrateTabFeature4")}
											</li>
										</ul>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideDetailsSessionsTitle")}
							</h4>
							<p className="text-sm text-base-content/70 mb-3">
								{t("help.scheduleGuideDetailsSessionsText")}
							</p>
							<div className="bg-base-100 border border-base-200 rounded-lg p-4">
								<h5 className="font-medium text-sm text-base-content mb-2">
									{t("help.scheduleGuideDetailsSessionsInfoTitle")}
								</h5>
								<ul className="text-sm text-base-content/70 space-y-1">
									<li>• {t("help.scheduleGuideDetailsSessionsInfo1")}</li>
									<li>• {t("help.scheduleGuideDetailsSessionsInfo2")}</li>
									<li>• {t("help.scheduleGuideDetailsSessionsInfo3")}</li>
									<li>• {t("help.scheduleGuideDetailsSessionsInfo4")}</li>
									<li>• {t("help.scheduleGuideDetailsSessionsInfo5")}</li>
									<li>• {t("help.scheduleGuideDetailsSessionsInfo6")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideDetailsEnrollmentsTitle")}
							</h4>
							<p className="text-sm text-base-content/70 mb-3">
								{t("help.scheduleGuideDetailsEnrollmentsText")}
							</p>
							<div className="bg-base-100 border border-base-200 rounded-lg p-4">
								<h5 className="font-medium text-sm text-base-content mb-2">
									{t("help.scheduleGuideDetailsEnrollmentsDetailsTitle")}
								</h5>
								<ul className="text-sm text-base-content/70 space-y-1">
									<li>• {t("help.scheduleGuideDetailsEnrollmentsDetail1")}</li>
									<li>• {t("help.scheduleGuideDetailsEnrollmentsDetail2")}</li>
									<li>• {t("help.scheduleGuideDetailsEnrollmentsDetail3")}</li>
									<li>• {t("help.scheduleGuideDetailsEnrollmentsDetail4")}</li>
								</ul>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.scheduleGuideDetailsImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Enrollment Migration System */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Repeat className="h-5 w-5" />
						<span>{t("help.scheduleGuideMigrationTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.scheduleGuideMigrationText")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideMigrationWhenTitle")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.scheduleGuideMigrationWhen1")}</li>
								<li>• {t("help.scheduleGuideMigrationWhen2")}</li>
								<li>• {t("help.scheduleGuideMigrationWhen3")}</li>
								<li>• {t("help.scheduleGuideMigrationWhen4")}</li>
								<li>• {t("help.scheduleGuideMigrationWhen5")}</li>
							</ul>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideMigrationProcessTitle")}
							</h4>
							<ol className="list-decimal list-inside space-y-2 text-sm text-base-content/70">
								<li>{t("help.scheduleGuideMigrationProcessStep1")}</li>
								<li>{t("help.scheduleGuideMigrationProcessStep2")}</li>
								<li>{t("help.scheduleGuideMigrationProcessStep3")}</li>
								<li>{t("help.scheduleGuideMigrationProcessStep4")}</li>
								<li>{t("help.scheduleGuideMigrationProcessStep5")}</li>
								<li>{t("help.scheduleGuideMigrationProcessStep6")}</li>
								<li>{t("help.scheduleGuideMigrationProcessStep7")}</li>
							</ol>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideMigrationFeaturesTitle")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.scheduleGuideMigrationFeature1Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.scheduleGuideMigrationFeature1Text")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.scheduleGuideMigrationFeature2Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.scheduleGuideMigrationFeature2Text")}
										</p>
									</div>
								</div>
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.scheduleGuideMigrationFeature3Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.scheduleGuideMigrationFeature3Text")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.scheduleGuideMigrationFeature4Title")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.scheduleGuideMigrationFeature4Text")}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideMigrationValidationTitle")}
							</h4>
							<div className="space-y-3">
								<div className="bg-error/10 border border-error/20 rounded-lg p-3">
									<h5 className="font-medium text-error text-sm mb-1">
										{t("help.scheduleGuideMigrationErrorTitle")}
									</h5>
									<ul className="text-sm text-error/80 space-y-1">
										<li>• {t("help.scheduleGuideMigrationError1")}</li>
										<li>• {t("help.scheduleGuideMigrationError2")}</li>
										<li>• {t("help.scheduleGuideMigrationError3")}</li>
										<li>• {t("help.scheduleGuideMigrationError4")}</li>
									</ul>
								</div>
								<div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
									<h5 className="font-medium text-warning text-sm mb-1">
										{t("help.scheduleGuideMigrationWarningTitle")}
									</h5>
									<ul className="text-sm text-warning/80 space-y-1">
										<li>• {t("help.scheduleGuideMigrationWarning1")}</li>
										<li>• {t("help.scheduleGuideMigrationWarning2")}</li>
										<li>• {t("help.scheduleGuideMigrationWarning3")}</li>
										<li>• {t("help.scheduleGuideMigrationWarning4")}</li>
									</ul>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.scheduleGuideMigrationWorkflowTitle")}
							</h4>
							<div className="bg-info/10 border border-info/20 rounded-lg p-4">
								<div className="flex items-start space-x-3">
									<AlertCircle className="h-5 w-5 text-info mt-0.5" />
									<div>
										<h5 className="font-medium text-info">
											{t("help.scheduleGuideMigrationWorkflowSubtitle")}
										</h5>
										<ol className="text-sm text-info/80 mt-2 space-y-1">
											<li>
												1. {t("help.scheduleGuideMigrationWorkflowStep1")}
											</li>
											<li>
												2. {t("help.scheduleGuideMigrationWorkflowStep2")}
											</li>
											<li>
												3. {t("help.scheduleGuideMigrationWorkflowStep3")}
											</li>
											<li>
												4. {t("help.scheduleGuideMigrationWorkflowStep4")}
											</li>
											<li>
												5. {t("help.scheduleGuideMigrationWorkflowStep5")}
											</li>
											<li>
												6. {t("help.scheduleGuideMigrationWorkflowStep6")}
											</li>
										</ol>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.scheduleGuideMigrationImagePlaceholder")}</em>
						</p>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
